/**
 * Settings → Notifications → "Events I follow" (TRUS-685).
 *
 * A person's own subscription to system events, delivered to the bell and/or
 * as a push (contract C, personal subscriptions):
 *   GET/PUT /api/notifications/event-subscriptions/me
 *
 * Same catalog and filters as a channel's "Connect events", with two
 * differences: the assignees filter offers "Me", and member-data (PHI) types
 * are never locked — the server only notifies the person about members they
 * can see, and the notice carries no member details.
 *
 * Offered types: every org type, plus platform types the server marks
 * `can_subscribe` (platform admins in the operator org). A type already
 * followed stays listed so it can be turned off.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Bell, Loader2, Smartphone } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  EMPTY_FILTERS,
  systemEventsApi,
  type PersonalEventChannel,
  type SystemEventFilters,
  type SystemEventType,
  type SystemEventsApiError,
} from '@/services/systemEventsApi';
import {
  filterKindsFor,
  groupTypes,
  systemEventTypesKey,
  toggleValue,
  type TFn,
} from '@/components/messaging/systemEvents/eventCatalog';
import { EventTypeChecklist } from '@/components/messaging/systemEvents/EventTypeChecklist';
import { EventFilterFields } from '@/components/messaging/systemEvents/EventFilterFields';

export const myEventSubscriptionKey = ['my-event-subscription'] as const;

/** Types a person may follow: org types, and platform types the server allows. */
export function followableTypes(types: SystemEventType[], followed: string[] = []): SystemEventType[] {
  return types.filter((x) => x.scope === 'org' || x.can_subscribe || followed.includes(x.type));
}

export function eventsIFollowErrorMessage(err: unknown, t: TFn): string {
  const e = err as SystemEventsApiError | undefined;
  switch (e?.code) {
    case 'invalid_filter':
      return t(
        'notifications.eventsIFollow.errors.invalidFilter',
        'One of the programs or people in your filters is not in this organization any more. Check the filters and try again.',
      );
    case 'invalid_channels':
      return t('notifications.eventsIFollow.errors.invalidChannels', 'Pick the bell, push, or both.');
    case 'unknown_event_type':
      return t(
        'notifications.eventsIFollow.errors.unknownType',
        'One of these events is no longer available. Reload the page and try again.',
      );
    case 'cannot_subscribe':
      return t('notifications.eventsIFollow.errors.cannotSubscribe', 'You cannot follow one of these events.');
    default:
      break;
  }
  if (e?.status === 403) {
    return t('notifications.eventsIFollow.errors.cannotSubscribe', 'You cannot follow one of these events.');
  }
  return t('notifications.eventsIFollow.errors.generic', 'Something went wrong. Please try again.');
}

export function EventsIFollow() {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();

  const typesQuery = useQuery({
    queryKey: systemEventTypesKey,
    queryFn: () => systemEventsApi.listTypes(),
    staleTime: 5 * 60_000,
  });
  const subQuery = useQuery({
    queryKey: myEventSubscriptionKey,
    queryFn: () => systemEventsApi.getMySubscription(),
    retry: false,
  });

  const types = typesQuery.data ?? [];
  const saved = subQuery.data ?? null;

  const [selected, setSelected] = useState<string[]>([]);
  const [filters, setFilters] = useState<SystemEventFilters>(EMPTY_FILTERS);
  const [channels, setChannels] = useState<PersonalEventChannel[]>(['bell']);
  const [active, setActive] = useState(true);

  useEffect(() => {
    setSelected(saved?.event_types ?? []);
    setFilters(saved?.filters ?? EMPTY_FILTERS);
    setChannels(saved?.channels ?? ['bell']);
    setActive(saved ? saved.is_active : true);
  }, [saved]);

  const offered = useMemo(() => followableTypes(types, saved?.event_types), [types, saved]);
  const groups = useMemo(() => groupTypes(offered), [offered]);
  const offeredKinds = useMemo(() => filterKindsFor(offered), [offered]);
  const activeKinds = useMemo(
    () => filterKindsFor(types.filter((x) => selected.includes(x.type))),
    [types, selected],
  );

  const save = useMutation({
    mutationFn: () =>
      systemEventsApi.saveMySubscription({
        event_types: selected,
        filters: {
          repos: activeKinds.has('repos') ? filters.repos : [],
          services: activeKinds.has('services') ? filters.services : [],
          programs: activeKinds.has('programs') ? filters.programs : [],
          assignees: activeKinds.has('assignees') ? filters.assignees : [],
        },
        channels,
        is_active: active,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(myEventSubscriptionKey, next);
      toast.success(t('notifications.eventsIFollow.saved', 'Your event notifications are saved'));
    },
    onError: (err) => {
      toast.error(t('notifications.eventsIFollow.saveFailed', 'Could not save your event notifications'), {
        description: eventsIFollowErrorMessage(err, t),
      });
    },
  });

  const loading = typesQuery.isLoading || subQuery.isLoading;
  const busy = save.isPending;
  const needsChannel = selected.length > 0 && active && channels.length === 0;

  return (
    <Card className="card-elevated mb-6" data-testid="events-i-follow">
      <CardHeader>
        <CardTitle className="text-heading-4">{t('notifications.eventsIFollow.title', 'Events I follow')}</CardTitle>
        <CardDescription>
          {t(
            'notifications.eventsIFollow.description',
            'Get a bell notification or a push when these things happen in your organization.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="events-i-follow-loading">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t('notifications.eventsIFollow.loading', 'Loading…')}
          </div>
        ) : subQuery.isError ? (
          <p className="text-sm text-destructive" role="alert" data-testid="events-i-follow-error">
            {eventsIFollowErrorMessage(subQuery.error, t)}
          </p>
        ) : offered.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="events-i-follow-empty">
            {t('notifications.eventsIFollow.empty', 'There are no events you can follow yet.')}
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
              <label htmlFor="events-i-follow-active" className="text-sm font-medium">
                {active
                  ? t('notifications.eventsIFollow.on', 'On — notifying me')
                  : t('notifications.eventsIFollow.off', 'Off — paused')}
              </label>
              <Switch
                id="events-i-follow-active"
                checked={active}
                onCheckedChange={(v: boolean) => setActive(!!v)}
                aria-label={t('notifications.eventsIFollow.activeLabel', 'Notify me about these events')}
                disabled={busy}
              />
            </div>

            <EventTypeChecklist
              groups={groups}
              allTypes={types}
              selected={selected}
              onToggle={(type, on) => setSelected((s) => toggleValue(s, type, on))}
              disabled={busy}
              testIdPrefix="follow-type"
              phiNote={t(
                'notifications.eventsIFollow.phiNote',
                'You are only notified about members you can see. Notices never include member details.',
              )}
            />

            <EventFilterFields
              kinds={offeredKinds}
              value={filters}
              onChange={setFilters}
              disabled={busy}
              allowMe
              idPrefix="events-i-follow"
            />

            <fieldset className="space-y-2" data-testid="events-i-follow-channels">
              <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('notifications.eventsIFollow.channelsLegend', 'Notify me by')}
              </legend>
              {(
                [
                  ['bell', Bell, t('notifications.eventsIFollow.bell', 'Bell')],
                  ['push', Smartphone, t('notifications.eventsIFollow.push', 'Push')],
                ] as const
              ).map(([value, Icon, label]) => (
                <div key={value} className="flex items-center justify-between gap-3">
                  <label htmlFor={`events-i-follow-${value}`} className="flex items-center gap-2 text-sm">
                    <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    {label}
                  </label>
                  <Switch
                    id={`events-i-follow-${value}`}
                    checked={channels.includes(value)}
                    onCheckedChange={(v: boolean) => setChannels((c) => toggleValue(c, value, !!v))}
                    aria-label={label}
                    disabled={busy}
                    data-testid={`events-i-follow-channel-${value}`}
                  />
                </div>
              ))}
              {needsChannel && (
                <p className="text-xs text-destructive" role="alert" data-testid="events-i-follow-needs-channel">
                  {t('notifications.eventsIFollow.needsChannel', 'Turn on the bell, push, or both.')}
                </p>
              )}
            </fieldset>

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={() => save.mutate()}
                disabled={busy || needsChannel}
                data-testid="events-i-follow-save"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                {t('notifications.eventsIFollow.save', 'Save')}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default EventsIFollow;
